// client/src/components/web/WebChatWidget.jsx
//
// Widget de chat del Canal Web. Reutilizable en dos modos:
//  - variant="page":     ocupa todo el iframe (/iframe/:companyId/:channelId). El botón
//                        flotante y el minimizar los gestiona widget.js en la página
//                        anfitriona vía postMessage.
//  - variant="floating": dibuja su propio botón flotante circular + ventana, y controla
//                        abrir/cerrar internamente (para /p, /tienda, /cita, /ticket, /news).
//
// El chat se comporta como cualquier otro canal: arranca el flujo del canal, soporta
// nodos de mensaje, botones, media (imagen/video/pdf), esperar-respuesta, escalado a
// agente y transferencias. El transporte es socket.io (namespace /web).
import { useEffect, useRef, useState, useCallback } from 'react';
import { useWebChatSocket } from '../../hooks/useWebChatSocket';
import { alertIncoming, requestNotificationPermission, unlockAudio } from '../../utils/notify';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';

const DEFAULT_COLORS = {
  primary: '#2563eb',   // burbuja del cliente + acentos
  botBubble: '#f1f5f9', // burbuja del bot/agente
  botText: '#0f172a',
  header: '#2563eb',
  headerText: '#ffffff',
  background: '#ffffff',
};

// Ícono de agente con diadema (muñeco hablando con headset).
function HeadsetIcon({ size = 30, color = '#ffffff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <rect x="2.5" y="13" width="4" height="6" rx="2" fill={color} />
      <rect x="17.5" y="13" width="4" height="6" rx="2" fill={color} />
      <path d="M19.5 19v1a3 3 0 0 1-3 3H13" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="9" r="3.2" fill={color} opacity="0.9" />
      <path d="M6.5 18c.8 2.3 3 4 5.5 4s4.7-1.7 5.5-4" stroke={color} strokeWidth="0" />
    </svg>
  );
}

function fmtDur(s) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function formatStamp(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const date = d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' });
    return `${date} ${time}`;
  } catch {
    return '';
  }
}

// ─── Ventana de chat (formulario + hilo) ──────────────────────────────────────
function ChatWindow({ companyId, channelId, colors, variant, onMinimize }) {
  const [config, setConfig] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [phase, setPhase] = useState('form'); // 'form' | 'chat'
  const [form, setForm] = useState({});
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [answered, setAnswered] = useState({}); // idx de mensajes con botones ya respondidos
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const sessionToken = phase === 'chat' ? localStorage.getItem(`webchat:token:${companyId}:${channelId}`) : null;

  const onNewMessage = useCallback((data) => {
    const p = data?.payload || data;
    if (!p) return;
    // Sonido + notificación cada vez que le escriben al cliente (bot o agente).
    const preview = p.type && p.type !== 'text'
      ? `📎 ${p.type === 'audio' ? 'Nota de voz' : 'Archivo adjunto'}`
      : (p.content || '');
    alertIncoming('Nuevo mensaje', preview, { tag: 'webchat' });
    setMessages((prev) => [...prev, {
      side: 'left',
      type: p.type,
      subtype: p.subtype,
      content: p.content,
      media_url: p.media_url,
      buttons: p.buttons,
      items: p.items,
      cards: p.cards,
      url: p.url,
      displayText: p.displayText,
      header: p.header,
      footer: p.footer,
      created_at: p.created_at,
    }]);
  }, []);

  const { sendMessage } = useWebChatSocket(sessionToken, onNewMessage);

  // Cargar configuración del canal (campos del formulario).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/web/channel/${companyId}/${channelId}/config`);
        if (!r.ok) throw new Error('No disponible');
        const cfg = await r.json();
        if (!alive) return;
        setConfig(cfg);
        // Si ya hay una sesión previa (no recargó del todo), reanudar en chat.
        const prev = localStorage.getItem(`webchat:token:${companyId}:${channelId}`);
        if (prev) { setPhase('chat'); }
      } catch {
        if (alive) setConfig({ fields: [], title: 'Chat' });
      } finally {
        if (alive) setLoadingCfg(false);
      }
    })();
    return () => { alive = false; };
  }, [companyId, channelId]);

  // Al entrar a chat: reconstruir historial (por si fue un refresco de página).
  useEffect(() => {
    if (phase !== 'chat' || !sessionToken) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/web/history?token=${encodeURIComponent(sessionToken)}`);
        if (!r.ok) return;
        const { messages: hist } = await r.json();
        if (!alive || !Array.isArray(hist) || !hist.length) return;
        setMessages(hist.map((m) => ({
          side: m.send === 'client' ? 'right' : 'left',
          type: m.type,
          subtype: m.subtype,
          content: m.content,
          media_url: m.media_url,
          // Botones / lista / cta: se conservan para que el widget los redibuje como
          // botones apretables en el chat (igual que en tiempo real).
          buttons: m.buttons,
          items: m.items,
          cards: m.cards,
          url: m.url,
          displayText: m.displayText,
          header: m.header,
          footer: m.footer,
          created_at: m.created_at,
        })));
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [phase, sessionToken]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStart = async (e) => {
    e.preventDefault();
    setError('');
    const cleanPhone = phone.replace(/\D/g, '');
    if (!/^\d{7,15}$/.test(cleanPhone)) { setError('Ingresa un celular válido.'); return; }
    // Se envía el celular tal cual lo escribió (conservando un posible "+"): el servidor
    // le antepone el indicativo del país de la empresa si no lo trae.
    const phoneToSend = phone.trim();
    for (const f of (config.fields || [])) {
      const key = f.key || f.label;
      if (f.required && !String(form[key] || '').trim()) {
        setError(`El campo "${f.label || key}" es obligatorio.`); return;
      }
    }
    setStarting(true);
    // Gesto del usuario: desbloquea el audio y pide permiso de notificaciones para
    // poder avisarle cuando le respondan.
    unlockAudio();
    requestNotificationPermission();
    try {
      const r = await fetch(`${API_BASE}/api/web/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: Number(companyId), channelId: Number(channelId), phone: phoneToSend, fields: form }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'No se pudo iniciar.');
      localStorage.setItem(`webchat:token:${companyId}:${channelId}`, data.sessionToken);
      setPhase('chat');
    } catch (err) {
      setError(err.message);
    } finally {
      setStarting(false);
    }
  };

  const pushMine = (msg) => {
    setMessages((prev) => [...prev, { side: 'right', type: 'text', created_at: new Date().toISOString(), ...msg }]);
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    unlockAudio(); // mantiene el audio desbloqueado tras interactuar
    pushMine({ content: text });
    sendMessage(text, 'text');
    setInput('');
  };

  const handleButton = (idx, btn) => {
    pushMine({ content: btn.title || btn.id });
    sendMessage(btn.id, 'text'); // el content = id del botón (convención de todos los canales)
    setAnswered((a) => ({ ...a, [idx]: true }));
  };

  // ── Adjuntos y nota de voz (espeja client/src/components/agent/MessageInput.jsx) ──
  // WhatsApp/los demás canales no pueden descargar blobs locales: subimos el archivo a
  // una URL pública y enviamos esa URL con el mensaje.
  const uploadTo = async (endpoint, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${sessionToken}` },
      body: fd,
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    return data.url || null;
  };

  const sendMedia = async (file, type, content, endpoint) => {
    setUploading(true);
    try {
      const url = await uploadTo(endpoint, file);
      if (!url) { alert('No se pudo subir el archivo'); return; }
      pushMine({ type, content: content || null, media_url: url });
      sendMessage(content || '', type, url);
    } catch {
      alert('Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const type = file.type.startsWith('image/') ? 'image'
      : file.type.startsWith('video/') ? 'video' : 'file';
    sendMedia(file, type, type === 'file' ? file.name : null, '/api/web/upload');
  };

  const stopStream = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setRecording(false);
    setSeconds(0);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      cancelledRef.current = false;
      recorder.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const cancelled = cancelledRef.current;
        stopStream();
        if (cancelled) return;
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (!blob.size) return;
        const file = new File([blob], 'nota-voz.webm', { type: 'audio/webm' });
        await sendMedia(file, 'audio', null, '/api/web/audio/upload');
      };
      recorder.start();
      mediaRef.current = recorder;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      alert('No se pudo acceder al micrófono');
    }
  };

  const finishAndSend = () => { cancelledRef.current = false; mediaRef.current?.stop(); };
  const cancelRecording = () => { cancelledRef.current = true; mediaRef.current?.stop(); };

  // Colores: prop (tema de la vista propia) tiene prioridad; si no, los del canal
  // (config.colors) sobre los defaults.
  const c = { ...DEFAULT_COLORS, ...(config?.colors || {}), ...(colors || {}) };

  // ── Estilos ──
  const wrap = {
    display: 'flex', flexDirection: 'column', height: '100%', width: '100%',
    background: c.background, fontFamily: 'system-ui, -apple-system, sans-serif',
    overflow: 'hidden',
  };
  const header = {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
    background: c.header, color: c.headerText, flexShrink: 0,
  };

  return (
    <div style={wrap}>
      <div style={header}>
        <HeadsetIcon size={24} color={c.headerText} />
        <div style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>{config?.title || 'Chat'}</div>
        {/* 36×36 para que el toque en móvil no falle (antes el área era de ~26px). */}
        {onMinimize && (
          <button onClick={onMinimize} aria-label="Minimizar" title="Minimizar"
            style={{
              background: 'transparent', border: 'none', color: c.headerText, cursor: 'pointer',
              fontSize: 24, lineHeight: 1, padding: 0, width: 36, height: 36, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            —
          </button>
        )}
      </div>

      {loadingCfg ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>Cargando…</div>
      ) : phase === 'form' ? (
        <form onSubmit={handleStart} style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {config.welcome && <div style={{ fontSize: 14, color: '#475569' }}>{config.welcome}</div>}
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
            Celular *
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required
              style={inputStyle} placeholder="Tu número de celular" />
          </label>
          {(config.fields || []).map((f, i) => {
            const key = f.key || f.label;
            return (
              <label key={i} style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                {f.label || key}{f.required ? ' *' : ''}
                <input
                  type={f.type === 'email' ? 'email' : f.type === 'number' ? 'number' : 'text'}
                  value={form[key] || ''}
                  onChange={(e) => setForm((s) => ({ ...s, [key]: e.target.value }))}
                  required={!!f.required}
                  style={inputStyle}
                  placeholder={f.label || key}
                />
              </label>
            );
          })}
          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}
          <button type="submit" disabled={starting}
            style={{ marginTop: 4, padding: '11px 14px', background: c.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: starting ? 0.6 : 1 }}>
            {starting ? 'Iniciando…' : 'Iniciar conversación'}
          </button>
        </form>
      ) : (
        <>
          <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, background: '#f8fafc' }}>
            {messages.map((m, i) => (
              <Bubble key={i} m={m} idx={i} colors={c} answered={answered[i]} onButton={handleButton} />
            ))}
            <div ref={endRef} />
          </div>
          {recording ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, borderTop: '1px solid #e2e8f0', flexShrink: 0, background: c.background }}>
              <button onClick={cancelRecording} aria-label="Cancelar"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: '#dc2626', flexShrink: 0, padding: 4 }}>
                🗑️
              </button>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, color: '#334155', fontSize: 14 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#dc2626', display: 'inline-block' }} />
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>Grabando… {fmtDur(seconds)}</span>
              </div>
              <button onClick={finishAndSend} aria-label="Enviar nota de voz"
                style={{ width: 42, height: 42, borderRadius: '50%', background: c.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 18, flexShrink: 0 }}>
                ➤
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 10, borderTop: '1px solid #e2e8f0', flexShrink: 0, background: c.background }}>
              <input ref={fileRef} type="file" hidden onChange={handleFile} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                aria-label="Adjuntar archivo" title={uploading ? 'Subiendo…' : 'Adjuntar archivo'}
                style={{ background: 'transparent', border: 'none', cursor: uploading ? 'wait' : 'pointer', fontSize: 22, color: '#64748b', flexShrink: 0, padding: 4, opacity: uploading ? 0.4 : 1 }}>
                📎
              </button>
              <button onClick={startRecording} disabled={uploading}
                aria-label="Grabar nota de voz" title="Grabar nota de voz"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 22, color: '#64748b', flexShrink: 0, padding: 4, opacity: uploading ? 0.4 : 1 }}>
                🎤
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                placeholder={uploading ? 'Enviando archivo…' : 'Escribe un mensaje…'}
                style={{ flex: 1, minWidth: 0, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 20, fontSize: 14, outline: 'none' }}
              />
              <button onClick={handleSend} aria-label="Enviar"
                style={{ width: 42, height: 42, borderRadius: '50%', background: c.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 18, flexShrink: 0 }}>
                ➤
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const inputStyle = {
  display: 'block', width: '100%', marginTop: 4, padding: '9px 11px',
  border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

// ─── Carrusel ─────────────────────────────────────────────────────────────────
// El carrusel de WhatsApp es un componente nativo del cliente de Meta; aquí se dibuja
// como una fila de tarjetas desplazable (scroll-snap para el dedo en móvil + flechas
// para el ratón, ya que la ventana del chat es angosta).
function Carousel({ cards, colors, idx, answered, onButton }) {
  const railRef = useRef(null);

  const scrollBy = (dir) => {
    const rail = railRef.current;
    if (!rail) return;
    // Un "paso" = ancho de tarjeta + gap, para que el snap caiga en la siguiente.
    rail.scrollBy({ left: dir * (rail.firstChild?.offsetWidth || 200) + dir * 8, behavior: 'smooth' });
  };

  const arrow = (dir, label) => (
    <button onClick={() => scrollBy(dir)} aria-label={label}
      style={{
        width: 26, height: 26, borderRadius: '50%', border: `1px solid ${colors.primary}`,
        background: '#fff', color: colors.primary, cursor: 'pointer', fontSize: 13,
        lineHeight: 1, padding: 0, flexShrink: 0,
      }}>
      {dir < 0 ? '‹' : '›'}
    </button>
  );

  return (
    <div style={{ alignSelf: 'stretch', marginTop: 6 }}>
      <div ref={railRef}
        style={{
          display: 'flex', gap: 8, overflowX: 'auto', overflowY: 'hidden',
          scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
          paddingBottom: 6, scrollbarWidth: 'thin',
        }}>
        {cards.map((card, ci) => (
          <div key={ci}
            style={{
              flex: '0 0 200px', scrollSnapAlign: 'start', background: '#fff',
              border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}>
            {card.media?.url && (card.media.type === 'video' ? (
              <video src={card.media.url} controls style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block', background: '#000' }} />
            ) : (
              <a href={card.media.url} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                <img src={card.media.url} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
              </a>
            ))}
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              {card.body && (
                <div style={{ fontSize: 13, color: colors.botText, whiteSpace: 'pre-wrap', flex: 1 }}>{card.body}</div>
              )}
              {/* Tarjeta con CTA URL: enlace siempre activo (no se "responde"). */}
              {card.url ? (
                <a href={card.url} target="_blank" rel="noreferrer"
                  style={{
                    display: 'block', textAlign: 'center', padding: '7px 10px', borderRadius: 8,
                    background: colors.primary, color: '#fff', textDecoration: 'none',
                    fontSize: 13, fontWeight: 600,
                  }}>
                  {card.displayText || 'Abrir'}
                </a>
              ) : null}
              {/* Botones de respuesta rápida: se desactivan al responder, igual que los
                  botones de un mensaje interactivo normal. */}
              {!answered && (card.buttons || []).map((b, bi) => (
                <button key={bi} onClick={() => onButton(idx, b)}
                  style={{
                    background: '#fff', color: colors.primary, border: `1px solid ${colors.primary}`,
                    borderRadius: 8, padding: '7px 10px', fontSize: 13, cursor: 'pointer', width: '100%',
                  }}>
                  {b.title || b.id}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {cards.length > 1 && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-start' }}>
          {arrow(-1, 'Anterior')}
          {arrow(1, 'Siguiente')}
        </div>
      )}
    </div>
  );
}

// ─── Burbuja de mensaje ───────────────────────────────────────────────────────
function Bubble({ m, idx, colors, answered, onButton }) {
  const isRight = m.side === 'right';
  const bubble = {
    maxWidth: '80%',
    alignSelf: isRight ? 'flex-end' : 'flex-start',
    background: isRight ? colors.primary : colors.botBubble,
    color: isRight ? '#fff' : colors.botText,
    padding: '9px 12px', borderRadius: 14,
    borderBottomRightRadius: isRight ? 4 : 14,
    borderBottomLeftRadius: isRight ? 14 : 4,
    fontSize: 14, wordBreak: 'break-word', boxShadow: '0 1px 1px rgba(0,0,0,0.06)',
  };
  const stamp = {
    fontSize: 10.5, opacity: 0.7, marginTop: 4, textAlign: isRight ? 'right' : 'left',
  };

  const linkColor = isRight ? '#fff' : colors.primary;
  const media = m.media_url && (
    // Imagen: clicable → abre el original en una pestaña nueva (igual que el panel del agente).
    m.type === 'image' ? (
      <a href={m.media_url} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
        <img src={m.media_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />
      </a>
    )
    : m.type === 'video' ? <video src={m.media_url} controls style={{ maxWidth: '100%', borderRadius: 8 }} />
    : m.type === 'audio' ? <audio src={m.media_url} controls style={{ maxWidth: '100%' }} />
    // Documento/archivo: clic → lo descarga al equipo (o lo abre en una pestaña nueva si el
    // navegador lo previsualiza, p. ej. un PDF).
    : (
      // download con el nombre real del archivo (mismo origen que el iframe → se
      // respeta). En móvil se comporta igual: descarga o abre en pestaña nueva.
      <a href={m.media_url} target="_blank" rel="noreferrer" download={m.content || ''}
        style={{ color: linkColor, textDecoration: 'underline' }}>
        📎 {m.content || 'Descargar archivo'}
      </a>
    )
  );

  // Header de un mensaje interactivo (botones / lista / cta): imagen, video, documento
  // o texto en negrita, pintado sobre el cuerpo (igual que WhatsApp).
  const hdr = m.header;
  const headerNode = hdr && (
    hdr.type === 'image' && hdr.url ? (
      <a href={hdr.url} target="_blank" rel="noreferrer" style={{ display: 'block', marginBottom: 6 }}>
        <img src={hdr.url} alt="" style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />
      </a>
    )
    : hdr.type === 'video' && hdr.url ? (
      <video src={hdr.url} controls style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 6 }} />
    )
    : hdr.type === 'document' && hdr.url ? (
      <a href={hdr.url} target="_blank" rel="noreferrer" download
        style={{ display: 'inline-block', marginBottom: 6, color: linkColor, textDecoration: 'underline' }}>
        📎 Documento
      </a>
    )
    : hdr.type === 'text' && hdr.text ? (
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{hdr.text}</div>
    )
    : null
  );

  // Footer del mensaje interactivo: texto tenue debajo del cuerpo, sobre los botones.
  const footerNode = m.footer ? (
    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6, whiteSpace: 'pre-wrap' }}>{m.footer}</div>
  ) : null;

  // Carrusel: el body va en su burbuja y las tarjetas debajo, a lo ancho del hilo.
  const cards = (!isRight && m.subtype === 'carousel' && m.cards?.length) ? m.cards : null;
  // Con un carrusel sin body no hay nada que pintar en la burbuja: se omite.
  const hideBubble = cards && !m.content && !media && !headerNode && !footerNode;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isRight ? 'flex-end' : 'flex-start' }}>
      {!hideBubble && (
        <div style={bubble}>
          {media}
          {headerNode}
          {/* En documentos el nombre ya se muestra dentro del enlace descargable; no lo repetimos. */}
          {m.content && !(m.media_url && m.type !== 'image' && m.type !== 'video' && m.type !== 'audio') && (
            <div style={{ marginTop: (media || headerNode) ? 6 : 0, whiteSpace: 'pre-wrap' }}>{m.content}</div>
          )}
          {footerNode}
          {m.url && (
            <a href={m.url} target="_blank" rel="noreferrer"
              style={{ display: 'inline-block', marginTop: 8, color: colors.primary, background: '#fff', padding: '6px 10px', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>
              {m.displayText || 'Abrir'}
            </a>
          )}
        </div>
      )}
      {cards && (
        <Carousel cards={cards} colors={colors} idx={idx} answered={answered} onButton={onButton} />
      )}
      {/* Botones / lista de opciones (interactivos hasta que se responda) */}
      {!isRight && !answered && (m.buttons?.length || m.items?.length) ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, maxWidth: '85%' }}>
          {(m.buttons || m.items).map((b, i) => (
            <button key={i} onClick={() => onButton(idx, b)}
              style={{ background: '#fff', color: colors.primary, border: `1px solid ${colors.primary}`, borderRadius: 16, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
              {b.title || b.id}
            </button>
          ))}
        </div>
      ) : null}
      <div style={stamp}>{formatStamp(m.created_at)}</div>
    </div>
  );
}

// ─── Wrapper con variante ─────────────────────────────────────────────────────
export default function WebChatWidget({ companyId, channelId, colors, variant = 'floating' }) {
  const [open, setOpen] = useState(false);
  const c = colors || DEFAULT_COLORS;

  // Modo página (dentro del iframe): ocupa todo; minimizar avisa a widget.js.
  if (variant === 'page') {
    const minimize = () => {
      try { window.parent?.postMessage({ source: 'webchat', type: 'minimize' }, '*'); } catch { /* noop */ }
    };
    return (
      <div style={{ position: 'fixed', inset: 0 }}>
        <ChatWindow companyId={companyId} channelId={channelId} colors={c} variant="page" onMinimize={minimize} />
      </div>
    );
  }

  // Modo flotante (vistas propias): botón circular + ventana.
  const btn = {
    position: 'fixed', bottom: 20, left: 20, zIndex: 2147483000,
    width: 60, height: 60, borderRadius: '50%', background: c.primary, color: '#fff',
    border: 'none', cursor: 'pointer', boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const panel = {
    position: 'fixed', bottom: 20, left: 20, zIndex: 2147483000,
    width: 'min(380px, calc(100vw - 40px))', height: 'min(560px, calc(100vh - 40px))',
    borderRadius: 16, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
    background: c.background,
  };

  return open ? (
    <div style={panel}>
      <ChatWindow companyId={companyId} channelId={channelId} colors={c} variant="floating" onMinimize={() => setOpen(false)} />
    </div>
  ) : (
    <button style={btn} onClick={() => setOpen(true)} aria-label="Abrir chat">
      <HeadsetIcon size={30} color="#fff" />
    </button>
  );
}
